import { useEffect, useState, useCallback } from "react";
import { Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import UserAvatar from "@/components/UserAvatar";

/**
 * Notification bell + drawer. Polls /notifications/unread-count every 60s.
 * Opens a bottom Sheet showing the last 50 notifications.
 * Marks all read on open.
 */
export default function NotificationsBell({ className = "" }) {
  const nav = useNavigate();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadUnread = useCallback(() => {
    api.get("/notifications/unread-count").then(({ data }) => setUnread(data.unread || 0)).catch(() => {});
  }, []);

  useEffect(() => {
    loadUnread();
    const t = setInterval(loadUnread, 60_000);
    return () => clearInterval(t);
  }, [loadUnread]);

  async function openDrawer() {
    setOpen(true);
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data.notifications || []);
      // Mark all as read once the drawer is open.
      if ((data.unread || 0) > 0) {
        await api.post("/notifications/mark-read", {});
        setUnread(0);
      }
    } finally {
      setLoading(false);
    }
  }

  function handleClick(n) {
    setOpen(false);
    if (n.type === "new_character" && n.character_id) {
      nav(`/character/${n.character_id}`);
    } else if (n.type === "follow" && n.actor_name) {
      nav(`/u/${n.actor_name}`);
    }
  }

  return (
    <>
      <button
        data-testid="notifications-bell-btn"
        onClick={openDrawer}
        className={`relative flex h-10 w-10 items-center justify-center rounded-full bg-white/5 text-white/70 hover:bg-white/10 ${className}`}
        aria-label="Notifications"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span
            data-testid="notifications-unread-badge"
            className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="bottom"
          className="glass-strong border-white/10 text-white max-h-[80vh] rounded-t-3xl overflow-y-auto"
          data-testid="notifications-sheet"
        >
          <div className="mx-auto max-w-md pb-safe-4">
            <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-white/20" />
            <SheetHeader className="mb-4">
              <SheetTitle className="font-display text-2xl text-white text-center">Notifications</SheetTitle>
              <SheetDescription className="sr-only">Recent activity from people you follow.</SheetDescription>
            </SheetHeader>
            {loading ? (
              <div className="py-8 text-center text-neutral-500">Loading…</div>
            ) : notifs.length === 0 ? (
              <div data-testid="notifications-empty" className="py-12 text-center text-neutral-500">
                <Bell size={24} className="mx-auto mb-2 opacity-50" />
                <div className="text-sm">You're all caught up.</div>
              </div>
            ) : (
              <ul className="space-y-2">
                {notifs.map((n) => (
                  <li key={n.id}>
                    <button
                      data-testid={`notification-${n.id}`}
                      onClick={() => handleClick(n)}
                      className={`flex w-full items-start gap-3 rounded-2xl p-3 text-left transition ${
                        n.read ? "bg-white/[0.02] hover:bg-white/[0.04]" : "bg-violet-500/10 hover:bg-violet-500/15"
                      }`}
                    >
                      <UserAvatar src={n.actor_avatar} name={n.actor_name} size={36} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white">
                          <span className="font-medium">@{n.actor_name}</span>{" "}
                          {n.type === "follow" && <span className="text-neutral-300">started following you.</span>}
                          {n.type === "new_character" && (
                            <span className="text-neutral-300">
                              published a new character{n.character_name ? <>: <em className="not-italic text-violet-300">{n.character_name}</em></> : "."}
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5 text-[11px] text-neutral-500">
                          {timeAgo(n.created_at)}
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const s = Math.max(1, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
